import { Spacing } from '@persona-id/design-tokens';
import { EditIcon, PersonaIcon } from '@persona-id/design-tokens/icons';
import { isEmpty } from 'lodash/fp';
import { FC, Fragment, useCallback, useState } from 'react';
import styled from 'styled-components';

import DraggableDivider from 'dashboard/components/WorkflowControlPanel/DraggableDivider';
import ParamLabel from 'dashboard/components/WorkflowControlPanel/ParamLabel';
import ResultObject from 'dashboard/components/WorkflowControlPanel/ResultObject';
import StepConfigTypeCard from 'dashboard/components/WorkflowControlPanel/StepConfigTypeCard';
import TriggerAdvancedConfigurationPanel from 'dashboard/components/WorkflowControlPanel/TriggerAdvancedConfigurationPanel/TriggerAdvancedConfigurationPanel';
import TriggerAccountInputModalConfigurationPreview from 'dashboard/components/WorkflowControlPanel/TriggerConfigAccount/TriggerAccountInputModalConfigurationPreview';
import TriggerAccountInputModalPreview from 'dashboard/components/WorkflowControlPanel/TriggerConfigAccount/TriggerAccountInputModalPreview';
import CodeEditor from 'dashboard/components-lib/CodeEditor';
import MaximizablePanel from 'dashboard/components-lib/MaximizablePanel';
import { SAMPLE_TRIGGER_PARAMS_CONFIGURATION } from 'dashboard/models/account-action';
import { TriggerAccount, TriggerAccountInputModalConfiguration } from 'dashboard/models/trigger';
import { WorkflowError } from 'dashboard/models/workflow';
import { TriggerObject } from 'dashboard/models/workflow-run';
import { WorkflowRunStepKnownObjects } from 'dashboard/models/workflow-step';
import Button from 'design-system/components/Button';
import Spacer from 'design-system/components/Spacer';
import { HStack } from 'design-system/components/Stack';
import { Heading } from 'design-system/components/Text';
import useToggle from 'lib/hooks/useToggle';

const HorizontalContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex-grow: 1;
  justify-content: space-between;
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
`;

const CodeEditorContainer = styled.div`
  margin-right: ${Spacing[2]};
  height: 100%;
`;

interface TriggerConfigAccountProps {
  trigger: TriggerAccount;
  workflowName: string;
  editable: boolean;
  errors: WorkflowError[];
  triggerObject: TriggerObject | null;
  updateTrigger: (newTrigger: TriggerAccount) => void;
  handleClose: () => void;
  workflowVersionExpiration: number;
  updateWorkflowVersionExpiration: (expiration: number) => void;
}

const TriggerConfigAccount: FC<TriggerConfigAccountProps> = ({
  trigger,
  workflowName,
  editable,
  handleClose,
  updateTrigger,
  triggerObject,
  workflowVersionExpiration,
  updateWorkflowVersionExpiration,
}) => {
  const inputModalConfiguration = trigger.attributes['input-modal-configuration'];
  const [isPristine, setIsPristine] = useState(true);
  const [isEditorOpen, { disable: closeEditor, toggle: toggleEditor }] = useToggle(false);

  const updateInputModalConfiguration = useCallback(
    (newInputModalConfiguration: TriggerAccountInputModalConfiguration[]) => {
      setIsPristine(false);
      updateTrigger({
        ...trigger,
        attributes: {
          ...trigger.attributes,
          'input-modal-configuration': newInputModalConfiguration,
        },
      });
    },
    [updateTrigger, trigger],
  );

  return (
    <>
      <Container data-test='workflow-trigger-control-panel__control-panel-container'>
        <HorizontalContainer>
          <Heading size='sm'> Trigger Step </Heading>
          <Spacer height={2} />
        </HorizontalContainer>
        <Spacer height={2} />
        <StepConfigTypeCard icon={<PersonaIcon size={24} />} title='Account Trigger' />
        <Spacer height={2} />
        <Heading size='sm'> Configuration </Heading>
        {isEditorOpen && (
          <MaximizablePanel
            title='Input Modal Configuration'
            maximized={true}
            onOutsideClickAndEscPress={closeEditor}
          >
            {(fullScreen: boolean) => (
              <div style={{ height: fullScreen ? 'calc(100% - 48px)' : '450px' }}>
                <DraggableDivider
                  orientation='vertical'
                  firstComponent={
                    // TODO(zixiao): pass in schema of accountDataContext and triggerParams for autocompletion + update language to jsonjs
                    <CodeEditorContainer>
                      <CodeEditor
                        code={JSON.stringify(inputModalConfiguration, null, 2)}
                        onChange={(inputModalConfigurationJSON) => {
                          updateInputModalConfiguration(
                            inputModalConfigurationJSON
                              ? JSON.parse(inputModalConfigurationJSON)
                              : [],
                          );
                        }}
                        editable={editable}
                        language='json'
                      />
                    </CodeEditorContainer>
                  }
                  secondComponent={
                    <TriggerAccountInputModalPreview
                      workflowName={workflowName}
                      trigger={trigger}
                    />
                  }
                  firstMinWidth='10px'
                  secondMinWidth='10px'
                  firstStyle={{ height: '100%', overflow: 'scroll' }}
                  secondStyle={{ width: '100%' }}
                />
              </div>
            )}
          </MaximizablePanel>
        )}
        <Spacer height={1} />
        <ParamLabel
          labelText='Input Modal Configuration'
          htmlFor='trigger-config-account__edit-schema'
          buttons={[
            <Button
              id='trigger-config-account__edit-schema'
              aria-label='Edit'
              key='edit'
              width='icon'
              variant='basic'
              disabled={!editable}
              onClick={() => {
                if (!isEditorOpen && isPristine && isEmpty(inputModalConfiguration)) {
                  updateInputModalConfiguration(SAMPLE_TRIGGER_PARAMS_CONFIGURATION);
                }
                toggleEditor();
              }}
            >
              <EditIcon />
            </Button>,
          ]}
        />
        <Spacer height={1} />
        <TriggerAccountInputModalConfigurationPreview
          inputModalConfiguration={JSON.parse(JSON.stringify(inputModalConfiguration, null, 2))}
        />
      </Container>
      {triggerObject && (
        <Fragment>
          <Spacer height={3} />
          <ResultObject object={triggerObject as WorkflowRunStepKnownObjects} />
        </Fragment>
      )}
      <Spacer height={1} />
      <TriggerAdvancedConfigurationPanel
        editable={editable}
        workflowVersionExpiration={workflowVersionExpiration}
        updateWorkflowVersionExpiration={updateWorkflowVersionExpiration}
      />
      <Spacer height={3} />
      <HStack equal={true}>
        <Button id='workflow-trigger-control__close-button' onClick={handleClose}>
          Close
        </Button>
      </HStack>
    </>
  );
};

export default TriggerConfigAccount;
